import React, { useState, useEffect } from 'react';
import {
  Input,
  Button,
  Card,
  Image,
  Progress,
  Tag,
  Checkbox,
  Row,
  Col,
  message,
} from 'antd';
import { SearchOutlined, DeleteOutlined } from '@ant-design/icons';
import axios from 'axios';
import ReconnectingWebSocket from 'reconnecting-websocket';
import config from '../../config';
import { useTranslation } from 'react-i18next';

export default function ShopifyLinksRevamp() {
  const { t } = useTranslation();
  const [allProducts, setAllProducts] = useState([]);
  const [displayProducts, setDisplayProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(t('botTrainingPage.shopifySection.idle'));
  const [progress, setProgress] = useState(0);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [collections, setCollections] = useState(['All']);
  const [activeCollection, setActiveCollection] = useState('All');

  useEffect(() => {
    if (search.trim() === '') {
      setDisplayProducts(allProducts);
    } else {
      const filtered = allProducts.filter((product) =>
        product.name.toLowerCase().includes(search.toLowerCase())
      );
      setDisplayProducts(filtered);
    }
  }, [search, allProducts]);

  useEffect(() => {
    fetchStoredProducts();
  }, []);

  const fetchStoredProducts = () => {
    setStatus(t('botTrainingPage.shopifySection.loading'));
    axios
      .get(`${config.apiUrl}shopify/products/`, {
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
      })
      .then((res) => {
        const data = res.data;
        setAllProducts(data);
        setDisplayProducts(data);
        setStatus(data.length ? t('botTrainingPage.shopifySection.ready') : t('botTrainingPage.shopifySection.noProducts'));
      })
      .catch(() => {
        setStatus(t('botTrainingPage.shopifySection.noProducts'));
        setAllProducts([]);
        setDisplayProducts([]);
        setCollections(['All']);
      });
  };

  const startScrape = () => {
    setLoading(true);
    setStatus(t('botTrainingPage.shopifySection.starting'));
    axios
      .get(`${config.apiUrl}shopify/start_scraping/`, {
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
      })
      .then(() => {
        const ws = new ReconnectingWebSocket(
          `${config.websocketUrl}scrape-shopify/${JSON.parse(localStorage.getItem('UserObject')).id}`,
          [],
          { maxRetries: 10, reconnectInterval: 3000 }
        );

        ws.addEventListener('open', () => {
          const userId = JSON.parse(localStorage.getItem('UserObject')).id;
          ws.send(JSON.stringify({ user_id: userId }));
        });

        ws.addEventListener('message', (ev) => {
          const data = JSON.parse(ev.data);

          if (data.status === 'IN_PROGRESS' && data.products?.length > 0) {
            setAllProducts((prevAll) => {
              const existingIds = new Set(prevAll.map((p) => p.variant_id));
              const trulyNew = data.products.filter((p) => !existingIds.has(p.variant_id));
              return [...prevAll, ...trulyNew];
            });

            setProgress((prev) => prev + data.products.length);
            setStatus(`${t('botTrainingPage.shopifySection.scraping')} “${data.collection}” +${data.products.length}`);
          } else if (data.status === 'DONE') {
            setLoading(false);
            setStatus(t('botTrainingPage.shopifySection.done', { count: data.products_count }));
            fetchStoredProducts();
            ws.close();
          }
        });

        ws.addEventListener('error', () => {
          message.error(t('botTrainingPage.shopifySection.error'));
          setLoading(false);
          setStatus(t('botTrainingPage.shopifySection.error'));
        });
      })
      .catch(() => {
        setLoading(false);
        setStatus(t('botTrainingPage.shopifySection.failedToStart'));
        message.error(t('botTrainingPage.shopifySection.failedToStart'));
      });
  };

  const toggleSelect = (link) => {
    setSelected((prev) => {
      const s = new Set(prev);
      s.has(link) ? s.delete(link) : s.add(link);
      return s;
    });
  };

  const deleteSelected = () => {
    if (selected.size === 0) return;
    const toDelete = Array.from(selected);
    axios
      .delete(`${config.apiUrl}shopify/products/`, {
        headers: { Authorization: `Token ${localStorage.getItem('token')}` },
        data: { websites_url: toDelete },
      })
      .then((res) => {
        setAllProducts(res.data.remaining_links);
        setSelected(new Set());
        fetchStoredProducts();
      })
      .catch(() => {
        message.error(t('botTrainingPage.shopifySection.failedDelete'));
      });
  };

  return (
    <div style={{ padding: 24 }}>
      <Card title={t('botTrainingPage.shopifySection.title')} bordered>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Input
              placeholder={t('botTrainingPage.shopifySection.searchPlaceholder')}
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              allowClear
            />
          </Col>
          <Col>
            <Button type="primary" onClick={startScrape} loading={loading}>
              {loading ? t('botTrainingPage.shopifySection.scrapingButton') : t('botTrainingPage.shopifySection.scrape')}
            </Button>
          </Col>
        </Row>

        <Row style={{ marginTop: 16 }} gutter={[8, 8]}>
          {collections.map((col) => (
            <Col key={col}>
              <Tag
                color={col === activeCollection ? 'purple' : 'default'}
                onClick={() => setActiveCollection(col)}
                style={{ cursor: 'pointer' }}
              >
                {col}
              </Tag>
            </Col>
          ))}
        </Row>

        <div style={{ marginTop: 16, marginBottom: 16 }}>
          <span>{t('botTrainingPage.shopifySection.status')}: {status}</span>
          {selected.size > 0 && (
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={deleteSelected}
              style={{ float: 'right' }}
            >
              {t('botTrainingPage.shopifySection.delete')} ({selected.size})
            </Button>
          )}
        </div>

        <Row gutter={[16, 16]}>
          {displayProducts.length > 0 ? (
            displayProducts.map((prod) => (
              <Col key={prod.variant_id} xs={24} sm={12} md={8} lg={6}>
                <Card
                  hoverable
                  cover={
                    <Image
                      src={prod.image_url}
                      style={{ height: 180, objectFit: 'cover' }}
                      fallback="https://via.placeholder.com/300x180?text=No+Image"
                    />
                  }
                  actions={[
                    <Checkbox
                      checked={selected.has(prod.product_link)}
                      onChange={() => toggleSelect(prod.product_link)}
                    />,
                    <a href={prod.product_link} target="_blank" rel="noopener noreferrer">
                      {t('botTrainingPage.shopifySection.view')}
                    </a>,
                  ]}
                >
                  <Card.Meta
                    title={prod.name}
                    description={<span>${prod.price}</span>}
                  />
                </Card>
              </Col>
            ))
          ) : (
            <Col span={24} style={{ textAlign: 'center', padding: '40px 0' }}>
              <span style={{ color: '#888' }}>{status}</span>
            </Col>
          )}
        </Row>
      </Card>
    </div>
  );
}
